/**
 * Branding: what the product is called and what it looks like, for whoever is
 * using it.
 *
 *  - Signed in: the signed-in user's client (tenant).
 *  - Public chat widget: the chatbot's client.
 *  - Otherwise (sign-in pages): the product itself, from the frontend env.
 *
 * A client's branding is merged over the product's, so anything the client leaves
 * empty falls back to the product. A client that is not whitelabel only brings
 * its name, icon and favicon; the rest is always the product's.
 */
import { buildThemeCss, cleanColors, ThemeColors } from '../theme/engine';
import { PRODUCT } from '../theme/product';
import { fontOption, fontStack, fontStylesheet } from '../theme/fonts';
import { fetchImageUrl } from '../helpers/helper';

export interface StoredImage {
  key: string;
  bucket: string;
  contentType?: string;
}

/** The branding document auth-ms publishes for a tenant. */
export interface TenantBranding {
  tenantId: string;
  tenantSlug: string;
  whitelabel: boolean;
  productName: string;
  legalName: string | null;
  tagline: string | null;
  copyrightText: string | null;
  supportEmail: string | null;
  supportUrl: string | null;
  termsUrl: string | null;
  privacyUrl: string | null;
  logo: StoredImage | null;
  favicon: StoredImage | null;
  logoLightUrl: string | null;
  faviconUrl: string | null;
  colors: ThemeColors;
  fontFamily: string | null;
  version: number;
}

/** What the app renders from: the client's branding over the product's. */
export interface Branding {
  tenantId: string | null;
  whitelabel: boolean;
  productName: string;
  legalName: string | null;
  tagline: string | null;
  copyrightText: string | null;
  supportEmail: string | null;
  supportUrl: string | null;
  termsUrl: string | null;
  privacyUrl: string | null;
  logoLightUrl: string | null;
  /** No separate dark-background logo yet; kept for components that ask. */
  logoDarkUrl: string | null;
  faviconUrl: string | null;
  colors: ThemeColors;
  fontFamily: string | null;
  /** Product-level "Powered by" name; null hides the line. */
  poweredBy: string | null;
}

export const PRODUCT_BRANDING: Branding = {
  tenantId: null,
  whitelabel: false,
  productName: PRODUCT.name,
  legalName: PRODUCT.legalName,
  tagline: PRODUCT.tagline,
  copyrightText: null,
  supportEmail: PRODUCT.supportEmail,
  supportUrl: PRODUCT.supportUrl,
  termsUrl: PRODUCT.termsUrl,
  privacyUrl: PRODUCT.privacyUrl,
  logoLightUrl: PRODUCT.logoUrl,
  logoDarkUrl: null,
  faviconUrl: PRODUCT.faviconUrl,
  colors: PRODUCT.colors,
  fontFamily: PRODUCT.fontFamily,
  poweredBy: PRODUCT.poweredBy,
};

function apiBase(): string {
  return (process.env.REACT_APP_API_URL ?? process.env.REACT_APP_API_BASE_URL ?? '').replace(/\/+$/, '');
}

const CACHE_PREFIX = 'app_branding:';

function readCache(tenantId: string): TenantBranding | null {
  try {
    const raw = window.localStorage.getItem(CACHE_PREFIX + tenantId);
    return raw ? (JSON.parse(raw) as TenantBranding) : null;
  } catch {
    return null;
  }
}

function writeCache(branding: TenantBranding): void {
  try {
    window.localStorage.setItem(CACHE_PREFIX + branding.tenantId, JSON.stringify(branding));
  } catch {
    // Caching is an optimisation only.
  }
}

/** A tenant's published branding, or null when it cannot be loaded. */
export async function fetchTenantBranding(tenantId: string, signal?: AbortSignal): Promise<TenantBranding | null> {
  try {
    const response = await fetch(`${apiBase()}/auth/public/branding?tenantId=${encodeURIComponent(tenantId)}`, {
      signal,
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    const branding = (await response.json()) as TenantBranding;
    if (!branding || typeof branding.productName !== 'string') return null;
    writeCache(branding);
    return branding;
  } catch {
    return null;
  }
}

export function cachedTenantBranding(tenantId: string): TenantBranding | null {
  return readCache(tenantId);
}

async function imageUrl(image: StoredImage | null): Promise<string | null> {
  if (!image) return null;
  try {
    return await fetchImageUrl(image);
  } catch {
    return null;
  }
}

/** Merge a tenant's branding over the product's, resolving uploaded images. */
export async function mergeBranding(tenant: TenantBranding | null): Promise<Branding> {
  if (!tenant) return PRODUCT_BRANDING;

  const [logo, favicon] = await Promise.all([imageUrl(tenant.logo), imageUrl(tenant.favicon)]);
  const identity = {
    tenantId: tenant.tenantId,
    productName: tenant.productName || PRODUCT_BRANDING.productName,
    logoLightUrl: logo ?? tenant.logoLightUrl ?? PRODUCT_BRANDING.logoLightUrl,
    faviconUrl: favicon ?? tenant.faviconUrl ?? PRODUCT_BRANDING.faviconUrl,
  };

  if (!tenant.whitelabel) return { ...PRODUCT_BRANDING, ...identity };

  return {
    ...PRODUCT_BRANDING,
    ...identity,
    whitelabel: true,
    legalName: tenant.legalName ?? PRODUCT_BRANDING.legalName,
    tagline: tenant.tagline ?? PRODUCT_BRANDING.tagline,
    copyrightText: tenant.copyrightText,
    supportEmail: tenant.supportEmail ?? PRODUCT_BRANDING.supportEmail,
    supportUrl: tenant.supportUrl ?? PRODUCT_BRANDING.supportUrl,
    termsUrl: tenant.termsUrl ?? PRODUCT_BRANDING.termsUrl,
    privacyUrl: tenant.privacyUrl ?? PRODUCT_BRANDING.privacyUrl,
    colors: { ...PRODUCT_BRANDING.colors, ...cleanColors(tenant.colors as Record<string, unknown>) },
    fontFamily: tenant.fontFamily ?? PRODUCT_BRANDING.fontFamily,
  };
}

// ---------------------------------------------------------------------------
// Applying branding to the document
// ---------------------------------------------------------------------------

/** Read by the inline script in public/index.html to paint the theme before React. */
export const THEME_CACHE_KEY = 'app_theme';

function setManagedStyle(id: string, css: string): void {
  let style = document.getElementById(id) as HTMLStyleElement | null;
  if (!css) {
    style?.remove();
    return;
  }
  if (!style) {
    style = document.createElement('style');
    style.id = id;
    document.head.appendChild(style);
  }
  style.textContent = css;
}

function setManagedLink(id: string, rel: string, href: string | null): void {
  const existing = document.getElementById(id) as HTMLLinkElement | null;
  if (!href) {
    existing?.remove();
    return;
  }
  const link = existing ?? document.createElement('link');
  link.id = id;
  link.rel = rel;
  if (link.href !== href) link.href = href;
  if (!existing) document.head.appendChild(link);
}

function setFavicon(href: string | null): void {
  if (!href) return;
  document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]:not(#app-favicon)').forEach((node) => node.remove());
  setManagedLink('app-favicon', 'icon', href);
}

/** "Add to home screen" name and icon, from the same branding as everything else. */
function setManifest(branding: Branding): void {
  if (!branding.productName) return;
  const icon = branding.faviconUrl || branding.logoLightUrl;
  const manifest = {
    name: branding.productName,
    short_name: branding.productName.slice(0, 12),
    start_url: `${window.location.origin}/`,
    scope: `${window.location.origin}/`,
    display: 'standalone',
    theme_color: branding.colors.primary || '#ffffff',
    background_color: branding.colors.background || '#ffffff',
    icons: icon ? [{ src: icon, sizes: '192x192 512x512', purpose: 'any' }] : [],
  };
  setManagedLink(
    'app-manifest',
    'manifest',
    `data:application/manifest+json,${encodeURIComponent(JSON.stringify(manifest))}`
  );
  document.querySelectorAll('link[rel="manifest"]:not(#app-manifest)').forEach((node) => node.remove());
}

/**
 * Apply branding to the page: theme colours, font, title, favicon and the home
 * screen manifest. This is the only place any of them is set.
 */
export function applyBranding(branding: Branding): void {
  const css = buildThemeCss(branding.colors);
  setManagedStyle('app-theme', css);

  const font = fontOption(branding.fontFamily);
  document.documentElement.style.setProperty('--theme-font-family', font ? fontStack(font.name) : '');
  if (!font) document.documentElement.style.removeProperty('--theme-font-family');
  setManagedLink('app-font', 'stylesheet', font ? fontStylesheet(font) : null);

  if (branding.productName) document.title = branding.productName;
  setFavicon(branding.faviconUrl);
  setManifest(branding);

  try {
    window.localStorage.setItem(
      THEME_CACHE_KEY,
      JSON.stringify({ css, font: font ? fontStack(font.name) : null, title: branding.productName })
    );
  } catch {
    // Only used to avoid a flash of the default colours on the next load.
  }
}
